import { Avatar } from '@seek/avatar';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Column,
  Columns,
  Inline,
  Link,
  Stack,
} from 'braid-design-system';

import type { ProfileCardView } from '../../../types/profile.types';
import type { LoginPromptProps } from '../../GenericLoginPrompt/GenericLoginPrompt';
import { FakeWorkHistory } from '../FakeWorkHistory/FakeWorkHistory';
import { LastInteraction } from '../LastInteraction/LastInteraction';
import { Location } from '../Location/Location';
import { Name } from '../Name/Name';
import { Nationality } from '../Nationality/Nationality';
import { Salary } from '../Salary/Salary';
import { VerifiedCredentialBadge } from '../VerifiedCredentialBadge/VerifiedCredentialBadge';
import { WorkHistory } from '../WorkHistory/WorkHistory';

import translations from './.vocab';

interface Props<T extends ProfileCardView = ProfileCardView> {
  profile: T;
  isAuthenticated: boolean;
  loginUrl: string;
  profileViewLink: string;
  renderActions?: (profile: T) => React.ReactNode;
  onProfileClick?: (profileSlug: string) => void;
  trackingCallbacks: LoginPromptProps['trackingCallbacks'];
  onLastInteractionClick?: (profileSlug: string) => void;
}

export const ProfileCard = <T extends ProfileCardView = ProfileCardView>({
  profile,
  isAuthenticated,
  loginUrl,
  profileViewLink,
  renderActions,
  onProfileClick,
  trackingCallbacks,
  onLastInteractionClick,
}: Props<T>) => {
  const { t } = useTranslations(translations);

  const {
    slug,
    displayName,
    digitalIdentity,
    hasVerifiedCredentials,
    workHistories,
    homeLocation,
    salary,
    nationalities,
    hasEthicalHiring,
    lastInteractionDuration,
  } = profile;

  const shouldShowRealWorkHistories =
    workHistories?.length > 0 && isAuthenticated;

  const shouldShowFakeWorkHistories =
    workHistories?.length >= 1 && !isAuthenticated;

  return (
    <Link
      href={profileViewLink}
      onClick={(e: React.MouseEvent) => {
        // Only prevent default on left-click to open drawer
        if (e.button === 0) {
          e.preventDefault();
          onProfileClick?.(slug);
        }
        // Right-click (button === 2) will work normally for context menu
      }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box paddingX="medium" paddingY="large">
        <Columns space="small">
          <Column width="content" hideBelow="tablet">
            <Avatar name={displayName} variant="initials" size="large" />
          </Column>
          <Column>
            <Stack space="small">
              <Name
                hasEthicalHiring={Boolean(hasEthicalHiring)}
                displayName={displayName}
                digitalIdentity={digitalIdentity}
                isSignedIn={isAuthenticated}
              />
              {shouldShowRealWorkHistories && (
                <WorkHistory workHistories={workHistories} />
              )}

              {shouldShowFakeWorkHistories && (
                <FakeWorkHistory realWorkHistory={workHistories[0]} />
              )}
              {hasVerifiedCredentials && <VerifiedCredentialBadge />}

              <Stack space="xsmall">
                <Location homeLocation={homeLocation} />
                <Columns space="none" alignY="bottom" collapseBelow="tablet">
                  <Column width="content">
                    <Stack space="xsmall">
                      {nationalities ? (
                        <Nationality nationalities={nationalities} />
                      ) : null}
                      {salary ? <Salary salary={salary} /> : null}
                      {lastInteractionDuration ? (
                        <Box paddingTop="xsmall">
                          <LastInteraction
                            lastInteractionDuration={lastInteractionDuration}
                            onClick={() => onLastInteractionClick?.(slug)}
                          />
                        </Box>
                      ) : null}
                    </Stack>
                  </Column>
                  {renderActions ? (
                    <Column>
                      <Box
                        marginTop={{
                          mobile: 'medium',
                          tablet: 'none',
                          desktop: 'none',
                        }}
                      >
                        <Inline
                          align={{
                            mobile: 'left',
                            tablet: 'right',
                            desktop: 'right',
                          }}
                          space="none"
                        >
                          <Box>{renderActions(profile)}</Box>
                        </Inline>
                      </Box>
                    </Column>
                  ) : null}
                </Columns>
              </Stack>

              {!isAuthenticated && (
                <Box maxWidth="xsmall">
                  <Button
                    tone="neutral"
                    variant="soft"
                    size="small"
                    onClick={(e) => {
                      // We can't have <a> tags within <a> tags (top level link)
                      // So we use a button instead, and tell it to not trigger the <a>
                      // parent above. This does mean you can't context-open the
                      // login link, but that's a small price to pay.
                      e.preventDefault();
                      e.stopPropagation();

                      trackingCallbacks.onLoginPressed({
                        component: {
                          name: 'sign-in_navigation',
                          type: 'textLinkButton',
                        },
                      });

                      window.location.href = loginUrl;
                    }}
                  >
                    {t('SIGN_IN_FOR_FULL_WORK_HISTORY')}
                  </Button>
                </Box>
              )}
            </Stack>
          </Column>
        </Columns>
      </Box>
    </Link>
  );
};
